// src/pages/SingleBlogPostPage.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom'; // Import useParams to get slug
import client from '../contentfulClient';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types'; // Import for advanced rich text rendering options
import './SingleBlogPostPage.css'; // We'll create this CSS next

// Optional: Define custom rendering for rich text nodes if needed
const richTextRenderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <p className="blog-full-paragraph">{children}</p>,
    [BLOCKS.HEADING_3]: (node, children) => <h3 className="blog-full-h3">{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4 className="blog-full-h4">{children}</h4>,
    [BLOCKS.UL_LIST]: (node, children) => <ul className="blog-full-ul">{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol className="blog-full-ol">{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li className="blog-full-li">{children}</li>,
    [BLOCKS.HR]: () => <hr className="blog-full-hr" />,
    [BLOCKS.BLOCKQUOTE]: (node, children) => <blockquote className="blog-full-blockquote">{children}</blockquote>,
    [BLOCKS.EMBEDDED_ASSET]: (node) => { // For embedded images/media within rich text
      const { title, description, file } = node.data.target.fields;
      return (
        <img
          src={file.url}
          alt={description || title}
          className="blog-full-embedded-image"
        />
      );
    },
  },
  renderMark: {
    [MARKS.BOLD]: text => <strong className="blog-full-bold">{text}</strong>,
    [MARKS.ITALIC]: text => <em className="blog-full-italic">{text}</em>,
    // Add more as needed for other marks like underline, code, etc.
  },
};

const SingleBlogPostPage = () => {
  const { slug } = useParams(); // Get the slug from the URL
  const [blogPost, setBlogPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await client.getEntries({
          content_type: 'blogPost', // Your Content Model's API ID
          'fields.slug': slug,      // Query by the slug field
          limit: 1                  // We expect only one entry
        });

        if (response.items.length > 0) {
          setBlogPost(response.items[0]);
        } else {
          setError({ message: "Blog post not found." });
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching single blog post:", err);
        setError(err);
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]); // Re-fetch if slug changes

  if (loading) return <div className="blog-loading">Loading Roomease blog post...</div>;
  if (error) return <div className="blog-error">Error: {error.message} <Link to="/blogs">Go back to blog list</Link></div>;
  if (!blogPost) return <div className="blog-empty">Blog post not found.</div>; // Should be caught by error above, but good fallback

  return (
    <div className="roomease-single-blog-container">
      <Link to="/blogs" className="back-to-blogs-link">&larr; Back to all blogs</Link>

      <h1>{blogPost.fields.title}</h1>
      <p className="roomease-single-blog-author">By {blogPost.fields.author} on {new Date(blogPost.fields.publishDate).toLocaleDateString()}</p>

      {blogPost.fields.featuredImage && (
        <img
          src={blogPost.fields.featuredImage.fields.file.url}
          alt={blogPost.fields.featuredImage.fields.description || blogPost.fields.title}
          className="roomease-single-blog-featured-image"
        />
      )}

      <div className="roomease-single-blog-content">
        {documentToReactComponents(blogPost.fields.bodyContent, richTextRenderOptions)}
      </div>

      <Link to="/blogs" className="back-to-blogs-link bottom-link">&larr; Back to all blogs</Link>
    </div>
  );
};

export default SingleBlogPostPage;